/**
 * The base context menu item entity.
 *
 * @author Erik Galloway <egalloway@boltsystem.com>
 */
export default class ContextMenuItem {
    /**
     * Make a new ContextMenuItem instance.
     *
     * @param {GridApi} api
     * @param {Object} params
     */
    constructor(api, params) {
        this.api = api
        this.params = params

        this.attributes = {
            action: this.action(),
            name: this.name(),
            disabled: this.disabled(),
            shortcut: this.shortcut(),
            checked: this.checked(),
            icon: this.icon(),
            cssClasses: this.cssClasses(),
            tooltip: this.tooltip(),
            subMenu: this.subMenu(),
        }
    }

    /**
     * Get the action callback function.
     *
     * @return {void}
     */
    action() {
        throw new Error(
            'The ContextMenuItem.action() method must be implemented.'
        )
    }

    /**
     * Display a check beside the menu item option.
     *
     * @return {Boolean}
     */
    checked() {
        return false
    }

    /**
     * Custom CSS classes to apply to the menu item.
     *
     * @return {String[]}
     */
    cssClasses() {
        return []
    }

    /**
     * Determine if the menu item should be enabled/disabled.
     *
     * @return {Boolean}
     */
    disabled() {
        return false
    }

    /**
     * The icon to display, either a DOM element or HTML string.
     *
     * @return {HtmlElement|String}
     */
    icon() {
        return null
    }

    /**
     * Determine if the column is filterable.
     *
     * @return {Boolean}
     */
    isFilterable() {
        return !!this.params.column.colDef.filter
    }

    /**
     * The name of the menu item to display.
     *
     * @return {String}
     */
    name() {
        throw new Error(
            'The ContextMenuItem.name() method must be implemented.'
        )
    }

    /**
     * Keyboard shortcut display text (does not handle event binding).
     *
     * @return {String?}
     */
    shortcut() {
        return null
    }

    /**
     * Get the child submenu of the menu item.
     *
     * @return {Array?}
     */
    subMenu() {
        return null
    }

    /**
     * The tooltip to display on hover.
     *
     * @return {String?}
     */
    tooltip() {
        return null
    }

    /**
     * Get the object representation of the menu item.
     *
     * @return {Object}
     */
    toObject() {
        return this.attributes
    }
}
